import type { NotificationType } from '@shared/types';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { apiUrl } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext'; // To get the current user

const NOTIFICATION_TYPE_OPTIONS: {
  value: NotificationType;
  label: string;
  description: string;
}[] = [
  { value: 'NEW_ORGANIZATION', label: '組織新設', description: '新しい学部・学科等を設置します' },
  { value: 'STOP_RECRUITMENT', label: '募集停止', description: '既存の組織の学生募集を停止します' },
  { value: 'ABOLISH_ORGANIZATION', label: '組織廃止', description: '既存の組織を廃止します' },
];

export default function NotificationForm() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [notificationType, setNotificationType] = useState<NotificationType | ''>('');
  const [reason, setReason] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [forceApply, setForceApply] = useState(false);

  // Form fields for NEW_ORGANIZATION
  const [newOrgName, setNewOrgName] = useState('');
  // Parent ID selection - value used in payload, will add UI later
  const [newOrgParentId] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notificationType || !user) {
      alert('届け出の種類を選択し、ログインしている必要があります。');
      return;
    }

    let payload: Record<string, unknown> = {};
    if (notificationType === 'NEW_ORGANIZATION') {
      payload = { nameFormal: newOrgName, parentStructureId: newOrgParentId };
    }
    // Add payloads for other types here

    const notificationData = {
      type: notificationType,
      payload,
      reason,
      isSubmitted,
      forceApply,
      requestedBy: user.id,
    };

    try {
      const response = await fetch(apiUrl('/api/notifications'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(notificationData),
      });

      if (response.ok) {
        alert('届け出を申請しました。');
        navigate('/site/notifications');
      } else {
        const errorData = (await response.json()) as { message?: string };
        alert(`申請に失敗しました: ${errorData.message ?? 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to submit notification:', error);
      alert('申請中にエラーが発生しました。');
    }
  };

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-2xl font-bold mb-6">新規届け出</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Notification Type Selection */}
        <section className="bg-white border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">届け出の種類</h2>
          <div className="grid grid-cols-1 gap-3">
            {NOTIFICATION_TYPE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                  notificationType === opt.value ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="notificationType"
                  value={opt.value}
                  checked={notificationType === opt.value}
                  onChange={(e) => setNotificationType(e.target.value as NotificationType)}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-sm text-gray-500">{opt.description}</div>
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* Dynamic Form based on Type */}
        {notificationType === 'NEW_ORGANIZATION' && (
          <section className="bg-white border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">新設する組織の情報</h2>
            {/* Add fields for new organization */}
            <div>
              <label htmlFor="newOrgName" className="block text-sm font-medium text-gray-700 mb-1">
                組織の正式名称
              </label>
              <input
                type="text"
                id="newOrgName"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
                required
              />
            </div>
          </section>
        )}

        {/* Reason */}
        <section className="bg-white border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">申請理由</h2>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full border rounded-md px-3 py-2 h-24"
            placeholder="届け出の理由を記入してください（必須）"
            required
          />
        </section>

        {/* Master Data Flags */}
        <section className="bg-white border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">マスターデータへの反映</h2>
          <div className="space-y-4">
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={isSubmitted} onChange={(e) => setIsSubmitted(e.target.checked)} />
              <span>届け出が実施済み</span>
            </label>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={forceApply} onChange={(e) => setForceApply(e.target.checked)} />
              <span>届け出を未実施だが強制適用</span>
            </label>
          </div>
        </section>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Link to="/site/notifications">
            <Button type="button" variant="outline">
              キャンセル
            </Button>
          </Link>
          <Button type="submit">申請</Button>
        </div>
      </form>
    </div>
  );
}
